import React, { useRef, useEffect, useState, useCallback } from 'react';
import OpenSeadragon from 'openseadragon';
import { ImageViewerToolbar } from './ImageViewerToolbar';
import { ImageNavigator } from './ImageNavigator';
import { MeasurementTools } from './MeasurementTools';
import { AnnotationTools } from './AnnotationTools';
import { ImageAdjustments } from './ImageAdjustments';
import { Case } from '../types';

interface PathologyImageViewerProps {
  images: Case['images'];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  className?: string;
}

interface ImageMetadata {
  type?: string;
  imageType?: string;
  staining?: string;
  stainingInfo?: string;
  magnification?: string | number;
  caption?: string;
}

export function PathologyImageViewer({ 
  images, 
  currentIndex, 
  onClose, 
  onNext, 
  onPrevious,
  className = ''
}: PathologyImageViewerProps) {
  const viewerRef = useRef<HTMLDivElement>(null);
  const osdViewerRef = useRef<OpenSeadragon.Viewer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Phase 2 feature states
  const [showMeasurementTools, setShowMeasurementTools] = useState(false);
  const [showAnnotationTools, setShowAnnotationTools] = useState(false);
  const [showImageAdjustments, setShowImageAdjustments] = useState(false);
  const [pixelsPerMicron, setPixelsPerMicron] = useState(0.25); // Default calibration
  
  // UI visibility controls
  const [showToolbar, setShowToolbar] = useState(true);
  const [showNavigator, setShowNavigator] = useState(true);
  const [showPhase2Tools, setShowPhase2Tools] = useState(true);
  const [showVisibilityControls, setShowVisibilityControls] = useState(true);

  const currentImage = images?.[currentIndex];

  const IMAGE_TYPE_LABELS: Record<string, string> = {
    'he': 'Hematoxylin/Eosin (H&E)',
    'histochemical': 'Ιστοχημικές χρώσεις',
    'immunohistochemistry': 'Ανοσοϊστοχημεία',
    'molecular': 'Μοριακή',
    'gross': 'Μακροσκοπική'
  };

  // Initialize OpenSeadragon viewer
  const initializeViewer = useCallback(() => {
    if (!viewerRef.current || !currentImage) return;

    try {
      setIsLoading(true);
      setError(null);

      // Destroy existing viewer if it exists
      if (osdViewerRef.current) {
        osdViewerRef.current.destroy();
        osdViewerRef.current = null;
      }

      // Create new OpenSeadragon viewer
      osdViewerRef.current = OpenSeadragon({
        element: viewerRef.current,
        prefixUrl: 'https://cdn.jsdelivr.net/npm/openseadragon@4.1.0/build/openseadragon/images/',
        
        // Viewer settings
        tileSources: {
          type: 'image',
          url: currentImage.imageUrl,
          buildPyramid: false
        },
        
        // UI Controls
        showNavigationControl: false, // We'll use custom controls
        showZoomControl: false,
        showHomeControl: false,
        showFullPageControl: false,
        showRotationControl: false,
        showSequenceControl: false,
        
        // Navigation settings
        panHorizontal: true,
        panVertical: true,
        constrainDuringPan: false,
        wrapHorizontal: false,
        wrapVertical: false,
        
        // Zoom settings
        visibilityRatio: 0.5,
        minZoomLevel: 0.1,
        maxZoomLevel: 40,
        zoomPerClick: 1.0, // Disable click-to-zoom for measurement tools
        zoomPerScroll: 1.2,
        
        // Animation settings
        animationTime: 0.5,
        blendTime: 0.1,
        alwaysBlend: false,
        autoHideControls: true,
        
        // Touch/Gesture settings
        gestureSettingsPan: {
          scrollToZoom: true,
          clickToZoom: false, // Disable single click zoom for measurement tools
          dblClickToZoom: true, // Keep double-click zoom
          pinchToZoom: true,
          flickEnabled: true,
          flickMinSpeed: 120,
          flickMomentum: 0.25,
        },
        
        // Performance settings
        immediateRender: false,
        preserveImageSizeOnResize: false,
        preserveViewport: false,
        preserveOverlays: false,
        
        // Loading settings
        timeout: 120000,
        useCanvas: true,
        
        // Accessibility
        tabIndex: 0
      });

      // Event handlers
      osdViewerRef.current.addHandler('open', () => {
        setIsLoading(false);
        console.log('OpenSeadragon viewer opened successfully');
      });

      osdViewerRef.current.addHandler('open-failed', (event: any) => {
        setIsLoading(false);
        setError(`Αποτυχία φόρτωσης εικόνας: ${event.message || 'Άγνωστο σφάλμα'}`);
        console.error('OpenSeadragon open failed:', event);
      });

      osdViewerRef.current.addHandler('tile-load-failed', (event: any) => {
        console.warn('Tile load failed:', event);
      });

      // Handle fullscreen changes
      osdViewerRef.current.addHandler('full-screen', (event: any) => {
        setIsFullscreen(event.fullScreen);
      });

    } catch (err) {
      setIsLoading(false);
      setError('Σφάλμα κατά την αρχικοποίηση του viewer');
      console.error('Failed to initialize OpenSeadragon:', err);
    }
  }, [currentImage]);

  // Initialize viewer when image changes
  useEffect(() => {
    initializeViewer();
    
    // Cleanup on unmount
    return () => {
      if (osdViewerRef.current) {
        try {
          osdViewerRef.current.destroy();
          osdViewerRef.current = null;
        } catch (err) {
          console.error('Error destroying OpenSeadragon viewer:', err);
        }
      }
    };
  }, [initializeViewer]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!osdViewerRef.current) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'ArrowRight':
          e.preventDefault();
          onNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onPrevious();
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
          e.preventDefault();
          handleZoomOut();
          break;
        case '0':
          e.preventDefault();
          handleZoomHome();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          handleToggleFullscreen();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [onClose, onNext, onPrevious]);

  // Toolbar action handlers
  const handleZoomIn = () => {
    if (osdViewerRef.current) {
      const currentZoom = osdViewerRef.current.viewport.getZoom();
      osdViewerRef.current.viewport.zoomTo(currentZoom * 1.5);
    }
  };

  const handleZoomOut = () => {
    if (osdViewerRef.current) {
      const currentZoom = osdViewerRef.current.viewport.getZoom();
      osdViewerRef.current.viewport.zoomTo(currentZoom / 1.5);
    }
  };

  const handleZoomHome = () => {
    if (osdViewerRef.current) {
      osdViewerRef.current.viewport.goHome();
    }
  };

  const handleZoomToLevel = (level: number) => {
    if (osdViewerRef.current) {
      const homeZoom = osdViewerRef.current.viewport.getHomeZoom();
      osdViewerRef.current.viewport.zoomTo(homeZoom * level);
    }
  };

  const handleToggleFullscreen = () => {
    if (osdViewerRef.current) {
      if (osdViewerRef.current.isFullPage()) {
        osdViewerRef.current.setFullPage(false);
      } else {
        osdViewerRef.current.setFullPage(true);
      }
    }
  };

  if (!currentImage) return null;

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-95 z-50 flex flex-col ${className}`}>
      {/* Header with metadata and controls */}
      <div className="relative z-10 bg-black bg-opacity-80 backdrop-blur-sm">
        {/* Image metadata overlay - moved to top right to avoid toolbar overlap */}
        {(currentImage.type || currentImage.imageType) && (
          <div className="absolute top-4 right-20 md:right-20 sm:right-16 z-10">
            <div className="bg-black bg-opacity-80 text-white px-3 py-2 md:px-4 md:py-3 rounded-lg font-medium text-sm md:text-base backdrop-blur-sm border border-white/30 shadow-lg max-w-xs">
              <div className="font-semibold text-blue-300 mb-1">
                {IMAGE_TYPE_LABELS[currentImage.type || currentImage.imageType] || currentImage.type || currentImage.imageType}
              </div>
              
              {/* Staining information */}
              {(currentImage.staining || currentImage.stainingInfo) && (
                (currentImage.type === 'immunohistochemistry' || currentImage.type === 'histochemical') && (
                  <div className="text-sm text-yellow-200 font-medium mt-2 bg-black bg-opacity-50 px-2 py-1 rounded">
                    🔬 Χρώση: {currentImage.staining || currentImage.stainingInfo}
                  </div>
                )
              )}
              
              {/* Magnification information */}
              {currentImage.magnification && (
                <div className="text-sm text-green-200 font-medium mt-2 bg-black bg-opacity-50 px-2 py-1 rounded">
                  🔍 Μεγέθυνση: {currentImage.magnification}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 text-2xl z-10 bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center touch-manipulation min-h-[44px] min-w-[44px] backdrop-blur-sm transition-colors"
        >
          ✕
        </button>

        {/* Navigation arrows */}
        {images && images.length > 1 && (
          <>
            <button
              onClick={onPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 text-3xl z-10 bg-black bg-opacity-50 rounded-full w-14 h-14 flex items-center justify-center touch-manipulation min-h-[44px] min-w-[44px] backdrop-blur-sm transition-colors"
            >
              ‹
            </button>
            <button
              onClick={onNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 text-3xl z-10 bg-black bg-opacity-50 rounded-full w-14 h-14 flex items-center justify-center touch-manipulation min-h-[44px] min-w-[44px] backdrop-blur-sm transition-colors"
            >
              ›
            </button>
          </>
        )}


        {/* Toolbar - Conditionally visible */}
        {showToolbar && (
          <ImageViewerToolbar
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomHome={handleZoomHome}
            onZoomToLevel={handleZoomToLevel}
            onToggleFullscreen={handleToggleFullscreen}
            isFullscreen={isFullscreen}
          />
        )}
        
        {/* Tools Toggle Bar - Always visible for advanced viewer */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-black bg-opacity-80 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 px-2 py-1 flex space-x-2">
            {/* Zoom Toolbar Toggle */}
            <button
              onClick={() => setShowToolbar(!showToolbar)}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                showToolbar 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
              title={showToolbar ? 'Απόκρυψη Zoom Εργαλειοθήκης' : 'Εμφάνιση Zoom Εργαλειοθήκης'}
            >
              🔧 Zoom
            </button>

            {/* Separator */}
            <div className="w-px h-6 bg-white/30 my-auto"></div>
            
            {/* Phase 2 Tools - Conditionally grouped */}
            {showPhase2Tools && (
              <>
                <button
                  onClick={() => setShowMeasurementTools(!showMeasurementTools)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    showMeasurementTools 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                  title="Εργαλεία Μέτρησης"
                >
                  📏 Μέτρηση
                </button>
                
                <button
                  onClick={() => setShowAnnotationTools(!showAnnotationTools)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    showAnnotationTools 
                      ? 'bg-yellow-600 text-white' 
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                  title="Εργαλεία Σχολιασμού"
                >
                  ✏️ Σχολιασμός
                </button>
                
                <button
                  onClick={() => setShowImageAdjustments(!showImageAdjustments)}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    showImageAdjustments 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                  title="Ρυθμίσεις Εικόνας"
                >
                  💡 Ρυθμίσεις
                </button>

                {/* Separator */}
                <div className="w-px h-6 bg-white/30 my-auto"></div>
              </>
            )}
            
            {/* Mini-map Toggle */}
            <button
              onClick={() => setShowNavigator(!showNavigator)}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                showNavigator 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
              title={showNavigator ? 'Απόκρυψη Mini-map' : 'Εμφάνιση Mini-map'}
            >
              🗺️ Map
            </button>
            
            {/* Group controls */}
            <div className="w-px h-6 bg-white/30 my-auto"></div>
            
            <button
              onClick={() => setShowPhase2Tools(!showPhase2Tools)}
              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                showPhase2Tools 
                  ? 'bg-orange-600 text-white' 
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
              title={showPhase2Tools ? 'Απόκρυψη Προηγμένων Εργαλείων' : 'Εμφάνιση Προηγμένων Εργαλείων'}
            >
              ⚙️
            </button>
            
            <button
              onClick={() => {
                // Clean view mode - hide everything except image
                setShowToolbar(false);
                setShowNavigator(false);
                setShowPhase2Tools(false);
                setShowMeasurementTools(false);
                setShowAnnotationTools(false);
                setShowImageAdjustments(false);
              }}
              className="px-2 py-1 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
              title="Καθαρή Προβολή - Απόκρυψη Όλων"
            >
              🖼️
            </button>
          </div>
        </div>
      </div>

      {/* Main viewer area */}
      <div className="flex-1 relative">
        {/* Loading state */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
            <div className="text-white text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p>Φόρτωση εικόνας...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
            <div className="text-white text-center max-w-md mx-auto px-6">
              <div className="text-red-400 text-4xl mb-4">⚠️</div>
              <h3 className="text-lg font-semibold mb-2">Σφάλμα φόρτωσης</h3>
              <p className="text-sm text-gray-300 mb-4">{error}</p>
              <button
                onClick={() => initializeViewer()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Επανάληψη
              </button>
            </div>
          </div>
        )}

        {/* OpenSeadragon container */}
        <div 
          ref={viewerRef} 
          className="w-full h-full"
          style={{ background: '#000' }}
        />

        {/* Mini-map navigator - Conditionally visible */}
        {showNavigator && (
          <ImageNavigator viewer={osdViewerRef.current} />
        )}
        
        {/* Phase 2 Tools - Only show if Phase 2 tools are enabled */}
        {showPhase2Tools && showMeasurementTools && (
          <MeasurementTools 
            viewer={osdViewerRef.current}
            pixelsPerMicron={pixelsPerMicron}
            onCalibrationChange={setPixelsPerMicron}
          />
        )}
        
        {showPhase2Tools && showAnnotationTools && currentImage && (
          <AnnotationTools
            viewer={osdViewerRef.current}
            imageId={currentImage.id || `image_${currentIndex}`}
            onSave={(annotations) => {
              // TODO: Save annotations to database
              console.log('Saving annotations:', annotations);
            }}
          />
        )}
        
        {showPhase2Tools && showImageAdjustments && (
          <ImageAdjustments viewer={osdViewerRef.current} />
        )}
      </div>

      {/* Footer with image info */}
      <div className="relative z-10 bg-black bg-opacity-80 backdrop-blur-sm p-4">
        <div className="text-white text-center">
          <p className="text-lg font-medium mb-2">
            {currentImage.caption || `Εικόνα ${currentIndex + 1}`}
          </p>
          {images && images.length > 1 && (
            <p className="text-sm text-gray-300">
              {currentIndex + 1} από {images.length}
            </p>
          )}
          
          {/* Keyboard shortcuts hint */}
          <div className="text-xs text-gray-400 mt-2 space-x-4">
            <span>ESC: Κλείσιμο</span>
            <span>←/→: Πλοήγηση</span>
            <span>+/-: Zoom</span>
            <span>0: Αρχική θέση</span>
            <span>F: Πλήρης οθόνη</span>
            <span>Διπλό κλικ: Zoom</span>
          </div>
        </div>
      </div>
    </div>
  );
}